import { slugCatalogName } from '../files/catalogPaths';
import {
  FlowEdge,
  FlowNode,
  FlowNodeData,
  GraphDevice,
  catalogWs2812Ref,
  flowToSettings,
  settingsToFlow,
  stacklightStripHandle,
} from './graph';
import {
  FlowCloudWidget,
  FlowFileDocument,
  extractFlowFileFromSettings,
  mergeBaseWithFlowFile,
} from './flowFile';

export type PeripheryWidgetType =
  | 'none'
  | 'temperature'
  | 'gps_map'
  | 'sml_meter'
  | 'nfc_tag'
  | 'distance'
  | 'stacklight'
  | 'switch'
  | 'bulb'
  | 'relay';

export type PeripheryFlowPlan = {
  flowMode: 'new' | 'existing';
  flowName: string;
  hostname: string;
  controllerId: string;
  device: GraphDevice;
  widget: PeripheryWidgetType;
  interval?: number;
};

type MqttPoint = {
  key: string;
  handle: string;
  topic: string;
  pollHandle?: string;
  dashHandle?: string;
  extraField?: keyof FlowCloudWidget;
};

function generated(data: FlowNodeData): FlowNodeData {
  return { ...data, generated: true };
}

export function suggestedFlowName(hostname: string, peripheralId: string): string {
  const host = slugCatalogName(hostname || 'device');
  const peri = slugCatalogName(peripheralId || 'peri');
  return slugCatalogName(`${host}-${peri}-cloud`);
}

export function suggestedFlowLabel(hostname: string, peripheralId: string): string {
  return `${hostname || 'device'} → ${peripheralId} → Cloud`;
}

export function uniqueFlowName(desired: string, taken: string[]): string {
  const names = new Set(taken.map((name) => slugCatalogName(name)));
  const base = slugCatalogName(desired);
  if (!names.has(base)) {
    return base;
  }
  let index = 2;
  while (names.has(`${base}-${index}`)) {
    index += 1;
  }
  return `${base}-${index}`;
}

export function widgetsForDeviceType(type: string): PeripheryWidgetType[] {
  switch (type) {
    case 'sht21':
    case 'modbus_slave':
      return ['temperature', 'none'];
    case 'gps':
      return ['gps_map', 'none'];
    case 'sml':
      return ['sml_meter', 'none'];
    case 'pn532':
      return ['nfc_tag', 'none'];
    case 'vl53l0x':
      return ['distance', 'none'];
    case 'stacklight':
      return ['stacklight', 'none'];
    case 'stacklight_legacy':
      return ['stacklight', 'none'];
    case 'gpio_relay':
      return ['relay', 'switch', 'none'];
    case 'gpio_buzzer':
      return ['switch', 'bulb', 'none'];
    case 'gpio_led':
      return ['bulb', 'switch', 'none'];
    default:
      return ['none'];
  }
}

function mqttPointsFor(device: GraphDevice): MqttPoint[] {
  const id = device.id;
  const type = device.type;
  if (type === 'sht21') {
    return [
      { key: 'temp', handle: 'temperature', topic: `{base}/${id}/temp`, pollHandle: 'poll:temperature', dashHandle: 'temp' },
      { key: 'rh', handle: 'humidity', topic: `{base}/${id}/rh`, pollHandle: 'poll:humidity', dashHandle: 'rh', extraField: 'topicRh' },
    ];
  }
  if (type === 'modbus_slave') {
    const points = Array.isArray(device.points) ? device.points : [];
    const keys = points.map((item) => String((item as { key?: string }).key || '').trim()).filter(Boolean);
    const listed = keys.length ? keys : ['temp', 'rh'];
    return listed.map((key) => {
      const climate = key === 'temp' || key === 'temperature' || key === 'rh' || key === 'humidity';
      return {
        key,
        handle: 'out',
        topic: `{base}/${id}/${key}`,
        pollHandle: 'poll',
        dashHandle: key === 'rh' || key === 'humidity' ? 'rh' : 'temp',
        extraField: key === 'rh' || key === 'humidity' ? 'topicRh' : undefined,
      };
    });
  }
  if (type === 'gps') {
    return [
      { key: 'lat', handle: 'lat', topic: `{base}/${id}/lat`, dashHandle: 'in' },
      { key: 'lon', handle: 'lon', topic: `{base}/${id}/lon`, extraField: 'topicLon' },
    ];
  }
  if (type === 'sml') {
    return [
      { key: 'energy', handle: 'energy', topic: `{base}/${id}/energy`, dashHandle: 'energy' },
      { key: 'power', handle: 'power', topic: `{base}/${id}/power`, dashHandle: 'power', extraField: 'topicPower' },
      { key: 'energy_reverse', handle: 'energy_reverse', topic: `{base}/${id}/energy_reverse`, dashHandle: 'reverse', extraField: 'topicReverse' },
    ];
  }
  if (type === 'victron') {
    return [
      { key: 'v', handle: 'v', topic: `{base}/${id}/v` },
      { key: 'i', handle: 'i', topic: `{base}/${id}/i` },
      { key: 'ppv', handle: 'ppv', topic: `{base}/${id}/ppv` },
      { key: 'soc', handle: 'soc', topic: `{base}/${id}/soc` },
    ];
  }
  if (type === 'pn532') {
    return [
      { key: 'uid', handle: 'uid', topic: `{base}/${id}/uid`, dashHandle: 'in' },
      { key: 'present', handle: 'present', topic: `{base}/${id}/present`, extraField: 'topicPresent' },
    ];
  }
  if (type === 'vl53l0x') {
    return [
      { key: 'mm', handle: 'mm', topic: `{base}/${id}/mm`, pollHandle: 'poll:mm', dashHandle: 'in' },
    ];
  }
  return [];
}

function findController(existing: FlowNode[], controllerId: string, hostname: string): FlowNode | undefined {
  const byId = existing.find((node) => node.id === `g:${controllerId}`);
  if (byId) {
    return byId;
  }
  const byHost = existing.find((node) => (
    node.data.kind === 'controller'
    && (node.data.role === hostname || node.data.label === hostname)
  ));
  if (byHost) {
    return byHost;
  }
  return existing.find((node) => node.data.kind === 'controller');
}

function findBus(existing: FlowNode[], kind: 'modbus' | 'i2c' | 'uart'): FlowNode | undefined {
  if (kind === 'modbus') {
    return existing.find((node) => node.data.deviceType === 'modbus');
  }
  return existing.find((node) => node.id === `b:${kind}` || node.data.deviceType === kind);
}

function edge(
  source: string,
  sourceHandle: string,
  target: string,
  targetHandle: string,
): FlowEdge {
  return {
    id: `e-${source}-${sourceHandle}-${target}-${targetHandle}`.replace(/[^A-Za-z0-9:_-]+/g, '_'),
    source,
    sourceHandle,
    target,
    targetHandle,
  };
}

function pollIntervalSec(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 60;
}

export function buildPeripheryBundle(args: {
  device: GraphDevice;
  hostname: string;
  controllerId: string;
  widget: PeripheryWidgetType;
  interval?: number;
  existingNodes?: FlowNode[];
  origin?: { x: number; y: number };
}): { nodes: FlowNode[]; edges: FlowEdge[]; widgetPatch?: FlowCloudWidget } {
  const existing = args.existingNodes || [];
  const origin = args.origin || { x: 40, y: 80 };
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];
  const used = new Set(existing.map((node) => node.id));
  const push = (node: FlowNode) => {
    if (used.has(node.id)) {
      return node.id;
    }
    used.add(node.id);
    nodes.push({ ...node, data: generated(node.data) });
    return node.id;
  };

  const existingController = findController(existing, args.controllerId, args.hostname);
  const controllerId = existingController?.id || `g:${args.controllerId || slugCatalogName(args.hostname)}`;
  if (!existingController) {
    push({
      id: controllerId,
      type: 'controller',
      position: { x: origin.x, y: origin.y },
      data: {
        kind: 'controller',
        label: args.hostname || 'Gerät',
        role: args.hostname || args.controllerId,
      },
    });
  }

  const x0 = origin.x;
  const y0 = origin.y;
  const type = args.device.type;
  let sourceId = '';
  let sourceHandle = 'out';

  if (type === 'stacklight') {
    const listed = Array.isArray(args.device.strips)
      ? (args.device.strips as unknown[]).map((item) => String(item || '').trim()).filter(Boolean)
      : [];
    const strips = listed.slice(0, 4);
    strips.forEach((stripId, index) => {
      if (used.has(`d:${stripId}`)) {
        return;
      }
      push({
        id: `d:${stripId}`,
        type: 'device',
        position: { x: x0 + 180, y: y0 + index * 70 },
        data: {
          kind: 'device',
          label: stripId,
          deviceType: 'ws2812',
          device: catalogWs2812Ref(stripId),
        },
      });
    });
    const slId = `s:${args.device.id}`;
    const topic = String(args.device.id);
    push({
      id: slId,
      type: 'device',
      position: { x: x0 + 380, y: y0 + 90 },
      data: {
        kind: 'device',
        label: args.device.id,
        deviceType: 'stacklight',
        signalName: args.device.id,
        topic,
        ownerId: controllerId,
        device: { id: slId, type: 'stacklight', strips },
      },
    });
    edges.push(edge(controllerId, 'out', slId, 'owner'));
    strips.forEach((stripId, index) => {
      edges.push(edge(`d:${stripId}`, 'out', slId, stacklightStripHandle(index)));
    });
    const mqttId = `m:${args.device.id}`;
    push({
      id: mqttId,
      type: 'device',
      position: { x: x0 + 580, y: y0 + 90 },
      data: {
        kind: 'device',
        label: topic,
        deviceType: 'mqtt',
        topic,
        subscribe: true,
        device: { id: 'mqtt', type: 'mqtt' },
      },
    });
    edges.push(edge(slId, 'out', mqttId, 'publish'));
    if (args.widget === 'stacklight') {
      const cloudId = `c:${mqttId}`;
      const dashId = `k:dash:${args.device.id}`;
      push({
        id: cloudId,
        type: 'cloudTransform',
        position: { x: x0 + 780, y: y0 + 90 },
        data: { kind: 'cloudTransform', label: 'Cloud' },
      });
      push({
        id: dashId,
        type: 'sink',
        position: { x: x0 + 980, y: y0 + 90 },
        data: {
          kind: 'sink',
          label: args.device.id,
          to: `dashboard.${args.device.id}`,
          widgetType: 'stacklight',
          topic,
          signalName: args.device.id,
        },
      });
      edges.push(edge(mqttId, 'subscribe', cloudId, 'in'));
      edges.push(edge(cloudId, 'out', dashId, 'in'));
      return {
        nodes,
        edges,
        widgetPatch: {
          id: args.device.id,
          type: 'stacklight',
          title: args.device.id,
          topic,
          signalName: args.device.id,
        },
      };
    }
    return { nodes, edges };
  }

  if (type === 'modbus_slave') {
    const existingBus = findBus(existing, 'modbus');
    const busId = existingBus?.id || 'd:rs485';
    if (!existingBus) {
      push({
        id: busId,
        type: 'device',
        position: { x: x0 + 180, y: y0 },
        data: {
          kind: 'device',
          label: 'Modbus',
          deviceType: 'modbus',
          ownerId: controllerId,
          device: { id: 'rs485', type: 'modbus', enable: true },
        },
      });
      edges.push(edge(controllerId, 'out', busId, 'owner'));
    }
    sourceId = `d:${args.device.id}`;
    push({
      id: sourceId,
      type: 'device',
      position: { x: x0 + 360, y: y0 },
      data: {
        kind: 'device',
        label: args.device.id,
        deviceType: 'modbus_slave',
        device: { id: args.device.id, type: 'modbus_slave', enable: true },
      },
    });
    edges.push(edge(busId, 'bus', sourceId, 'in'));
  } else if (type === 'sht21' || type === 'pn532' || type === 'vl53l0x') {
    const existingBus = findBus(existing, 'i2c');
    const busId = existingBus?.id || 'b:i2c';
    if (!existingBus) {
      push({
        id: busId,
        type: 'device',
        position: { x: x0 + 180, y: y0 },
        data: {
          kind: 'device',
          label: 'I2C',
          deviceType: 'i2c',
          ownerId: controllerId,
          device: { id: 'i2c', type: 'i2c' },
        },
      });
      edges.push(edge(controllerId, 'out', busId, 'owner'));
    }
    sourceId = `d:${args.device.id}`;
    push({
      id: sourceId,
      type: 'device',
      position: { x: x0 + 360, y: y0 },
      data: {
        kind: 'device',
        label: args.device.id,
        deviceType: type,
        device: { id: args.device.id, type, enable: true },
      },
    });
    edges.push(edge(busId, 'bus', sourceId, 'bus'));
  } else if (type === 'sml' || type === 'gps' || type === 'victron') {
    sourceId = `d:${args.device.id}`;
    push({
      id: sourceId,
      type: 'device',
      position: { x: x0 + 180, y: y0 },
      data: {
        kind: 'device',
        label: args.device.id,
        deviceType: type,
        ownerId: type === 'gps' ? undefined : controllerId,
        device: { id: args.device.id, type, enable: true },
      },
    });
    if (type === 'sml' || type === 'victron') {
      edges.push(edge(controllerId, 'out', sourceId, 'in'));
    }
  } else {
    sourceId = `d:${args.device.id}`;
    push({
      id: sourceId,
      type: 'device',
      position: { x: x0 + 180, y: y0 },
      data: {
        kind: 'device',
        label: args.device.id,
        deviceType: type,
        device: { id: args.device.id, type, enable: true },
      },
    });
    edges.push(edge(controllerId, 'out', sourceId, 'in'));
    return { nodes, edges };
  }

  const points = mqttPointsFor(args.device);
  const interval = pollIntervalSec(args.interval);
  const needPoll = type !== 'sml' && type !== 'victron' && points.some((point) => point.pollHandle);
  let triggerId = '';
  if (needPoll) {
    const existingTrigger = existing.find((node) => node.data.kind === 'trigger' && Number(node.data.interval) === interval);
    triggerId = existingTrigger?.id || `t:${interval}`;
    if (!existingTrigger) {
      push({
        id: triggerId,
        type: 'trigger',
        position: { x: x0, y: y0 + 220 },
        data: {
          kind: 'trigger',
          label: `${interval}s`,
          when: 'interval',
          interval,
        },
      });
    }
  } else if (type === 'sml' || type === 'victron') {
    const existingRx = existing.find((node) => node.data.kind === 'trigger' && node.data.when === 'event');
    triggerId = existingRx?.id || `t:rx:${args.device.id}`;
    if (!existingRx) {
      push({
        id: triggerId,
        type: 'trigger',
        position: { x: x0, y: y0 + 220 },
        data: {
          kind: 'trigger',
          label: 'RX',
          when: 'event',
        },
      });
    }
    edges.push(edge(triggerId, 'out', sourceId, 'rx'));
  }

  const widgetPatch: FlowCloudWidget = {
    id: args.device.id,
    type: args.widget === 'none' ? undefined : args.widget,
    title: args.device.id,
  };
  let dashId = '';
  if (args.widget !== 'none') {
    dashId = `k:dash:${args.device.id}`;
    push({
      id: dashId,
      type: 'sink',
      position: { x: x0 + 1100, y: y0 + (points.length > 1 ? 40 : 0) },
      data: {
        kind: 'sink',
        label: args.device.id,
        to: `dashboard.${args.device.id}`,
        widgetType: args.widget,
        topic: points[0]?.topic,
        topicRh: points.find((point) => point.extraField === 'topicRh')?.topic,
        topicPower: points.find((point) => point.extraField === 'topicPower')?.topic,
        topicReverse: points.find((point) => point.extraField === 'topicReverse')?.topic,
        signalName: args.widget === 'stacklight' ? args.device.id : undefined,
      },
    });
  }

  points.forEach((point, index) => {
    let fromId = sourceId;
    let fromHandle = point.handle;
    if (type === 'modbus_slave') {
      const pointId = `p:${args.device.id}:${point.key}`;
      push({
        id: pointId,
        type: 'point',
        position: { x: x0 + 520, y: y0 + index * 140 },
        data: {
          kind: 'point',
          label: point.key,
          busId: args.device.id,
          slave: { id: args.device.id },
          point: { key: point.key },
        },
      });
      edges.push(edge(sourceId, 'out', pointId, 'in'));
      fromId = pointId;
      fromHandle = 'out';
      if (triggerId && point.pollHandle) {
        edges.push(edge(triggerId, 'out', pointId, 'poll'));
      }
    } else if (triggerId && point.pollHandle) {
      edges.push(edge(triggerId, 'out', sourceId, point.pollHandle));
    }

    const mqttId = `m:${args.device.id}_${point.key}`;
    push({
      id: mqttId,
      type: 'device',
      position: { x: x0 + 720, y: y0 + index * 140 },
      data: {
        kind: 'device',
        label: point.topic,
        deviceType: 'mqtt',
        topic: point.topic,
        subscribe: true,
        device: { id: 'mqtt', type: 'mqtt' },
      },
    });
    edges.push(edge(fromId, fromHandle, mqttId, 'publish'));
    if (index === 0) {
      widgetPatch.topic = point.topic;
    }
    if (point.extraField) {
      (widgetPatch as Record<string, unknown>)[point.extraField] = point.topic;
    }
    if (dashId) {
      const cloudId = `c:${mqttId}`;
      push({
        id: cloudId,
        type: 'cloudTransform',
        position: { x: x0 + 920, y: y0 + index * 140 },
        data: { kind: 'cloudTransform', label: 'Cloud' },
      });
      edges.push(edge(mqttId, 'subscribe', cloudId, 'in'));
      edges.push(edge(cloudId, 'out', dashId, point.dashHandle || 'in'));
    }
  });

  if (args.widget === 'none') {
    return { nodes, edges };
  }
  return { nodes, edges, widgetPatch };
}

export function mergePeripheryIntoDoc(doc: FlowFileDocument, plan: PeripheryFlowPlan): FlowFileDocument {
  const graph = settingsToFlow(mergeBaseWithFlowFile({}, doc));
  const maxX = graph.nodes.reduce((max, node) => Math.max(max, node.position.x), 40);
  const bundle = buildPeripheryBundle({
    device: plan.device,
    hostname: plan.hostname,
    controllerId: plan.controllerId,
    widget: plan.widget,
    interval: plan.interval,
    existingNodes: graph.nodes,
    origin: { x: graph.nodes.length ? maxX + 80 : 40, y: 80 },
  });
  const nodeIds = new Set(graph.nodes.map((node) => node.id));
  const nodes = [...graph.nodes, ...bundle.nodes.filter((node) => !nodeIds.has(node.id))];
  const edgeKeys = new Set(graph.edges.map((item) => item.id));
  const edges = [...graph.edges, ...bundle.edges.filter((item) => !edgeKeys.has(item.id))];
  const next = flowToSettings(nodes, edges, {}, { routesOnly: true });
  const extracted = extractFlowFileFromSettings(next, doc.id || plan.flowName);
  if (bundle.widgetPatch?.id) {
    const widgets = [...(extracted.cloud?.widgets || [])];
    const index = widgets.findIndex((item) => item.id === bundle.widgetPatch?.id);
    if (index >= 0) {
      widgets[index] = { ...widgets[index], ...bundle.widgetPatch };
    } else {
      widgets.push(bundle.widgetPatch);
    }
    extracted.cloud = { ...extracted.cloud, widgets };
  }
  return {
    ...extracted,
    id: doc.id || plan.flowName,
    enable: doc.enable !== false,
    layouts: Array.from(new Set([...(doc.layouts || []), ...(extracted.layouts || [])])),
  };
}
